import React, { useState, useEffect } from 'react';

import { UserOutlined, SaveOutlined } from '@ant-design/icons';
import { Card, Form, Input, Button, Space, Alert, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@shared/PageHeader';

import { operatorApiFetch } from '@services/operator/apiService';

const OperatorsNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      // 假設有 warehouses API，如果沒有需要添加
      const response = await operatorApiFetch('warehouses');
      const data = await response.json();

      if (data.success && data.data) {
        setWarehouses(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
      // 如果 API 不存在，設置空陣列
      setWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  // 核心提交邏輯：呼叫建立 operator API 並回傳 data
  const submitValues = async (values) => {
    const response = await operatorApiFetch('operators', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operator: values
      }),
    });
    return await response.json();
  };

  // 將 errors 套回欄位
  const applyFieldErrors = (errors) => {
    const errorFields = Object.entries(errors).map(([field, messages]) => ({
      name: field,
      errors: messages
    }));
    form.setFields(errorFields);
  };

  // 主要提交：成功後導向該筆記錄（operator 無 show 頁，edit 即為 detail）
  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const data = await submitValues(values);

      if (data.success) {
        message.success(t(`common.error.${data.message}`) || t('common.message.create_success'));
        // operator 沒有獨立的 show 頁，list 本身也是以 edit 為 detail
        const recordId = data.data?.id;
        if (recordId) {
          navigate(`/operators/${recordId}/edit`, { replace: true });
        } else {
          navigate('/operators', { replace: true });
        }
      } else {
        // 處理驗證錯誤
        if (data.errors) {
          applyFieldErrors(data.errors);
        } else {
          // 如果沒有 field errors，顯示一般錯誤訊息
          message.error(t(`common.error.${data.message}`) || t('common.error.create_failed'));
        }
      }
    } catch (err) {
      console.error('Failed to create operator:', err);
      message.error(t('common.error.create_failed'));
    } finally {
      setSaving(false);
    }
  };

  // 儲存並新增下一筆：成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const data = await submitValues(values);

        if (data.success) {
          message.success(t(`common.error.${data.message}`) || t('common.message.create_success'));
          form.resetFields();
        } else {
          if (data.errors) {
            applyFieldErrors(data.errors);
          } else {
            message.error(t(`common.error.${data.message}`) || t('common.error.create_failed'));
          }
        }
      } catch (err) {
        console.error('Failed to create operator:', err);
        message.error(t('common.error.create_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  const handleBack = () => {
    navigate('/operators');
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <PageHeader items={['org_management', 'org_management.operators', { label: t('operator.pages.operators.new.title') }]} />

      <Card
        title={
          <Space>
            <UserOutlined />
            {t('operator.pages.operators.new.title')}
          </Space>
        }
      >
        <Alert
          message={t('operator.pages.operators.new.notice_title')}
          description={t('operator.pages.operators.new.notice_desc')}
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label={t('models.operator.name')}
            name="name"
            rules={[
              { required: true, message: t('common.validation.name_required') }
            ]}
          >
            <Input placeholder={t('common.placeholder.please_enter')} />
          </Form.Item>

          <Form.Item
            label={t('models.operator.username')}
            name="username"
            rules={[
              { required: true, message: t('common.validation.username_required') }
            ]}
          >
            <Input placeholder={t('common.placeholder.please_enter')} />
          </Form.Item>

          <Form.Item
            label={t('models.operator.email')}
            name="email"
            rules={[
              { type: 'email', message: t('common.validation.email_invalid') }
            ]}
          >
            <Input type="email" placeholder={t('common.placeholder.please_enter')} />
          </Form.Item>

          <Form.Item
            label={t('common.password')}
            name="password"
            rules={[
              { required: true, message: t('common.validation.password_required') },
              { min: 6, message: t('common.validation.password_min_length') }
            ]}
          >
            <Input.Password placeholder={t('common.placeholder.please_enter')} />
          </Form.Item>

          <Form.Item
            label={t('models.warehouse.name')}
            name="warehouse_id"
            rules={[
              { required: true, message: t('common.validation.warehouse_required') }
            ]}
          >
            <Select
              placeholder={t('common.placeholder.please_select')}
              loading={loadingWarehouses}
              options={warehouses.map(warehouse => ({
                value: warehouse.id,
                label: warehouse.name
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={savingAndAdding}
              >
                {t('common.button.create')}
              </Button>
              <Button
                loading={savingAndAdding}
                disabled={saving}
                onClick={handleSaveAndAddAnother}
              >
                {t('common.button.save_and_add_another')}
              </Button>
              <Button onClick={handleBack}>
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default OperatorsNew;
